import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Eye, EyeOff, CheckCircle, XCircle, MessageSquare } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RULES = [
  { label: "En az 10 karakter", test: (p) => p.length >= 10 },
  { label: "Büyük harf", test: (p) => /[A-Z]/.test(p) },
  { label: "Küçük harf", test: (p) => /[a-z]/.test(p) },
  { label: "Rakam", test: (p) => /[0-9]/.test(p) },
  { label: "Sembol (!'^$#%&/()=*-_)", test: (p) => /[!'^$#%&/()=*\-_]/.test(p) },
];

export default function InviteAccept() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();

  const [inviteInfo, setInviteInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/auth/invite/${token}`)
      .then(({ data }) => { setInviteInfo(data); setLoading(false); })
      .catch(() => { setError("Bu davet bağlantısı geçersiz veya daha önce kullanılmış."); setLoading(false); });
  }, [token]);

  const allRulesPassed = RULES.every(r => r.test(password));
  const passwordsMatch = password === confirm && confirm !== "";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!allRulesPassed) { setSubmitError("Şifre gereksinimlerini karşılamıyor."); return; }
    if (!passwordsMatch) { setSubmitError("Şifreler eşleşmiyor."); return; }

    setSubmitting(true);
    setSubmitError(null);
    try {
      const { data } = await axios.post(`${API}/auth/invite/${token}/accept`, { password });
      loginWithToken(data.token, data.user);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setSubmitError(err?.response?.data?.detail || "Bir hata oluştu.");
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="flex items-center gap-3 justify-center mb-8">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center">
            <MessageSquare size={20} className="text-white" />
          </div>
          <span className="font-bold text-slate-800 text-xl">Netlen Canlı Destek</span>
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 shadow-xl p-8">
          {loading ? (
            <div className="text-center py-8 text-slate-400 animate-pulse">Yükleniyor...</div>
          ) : error ? (
            <div className="text-center py-8">
              <XCircle size={40} className="text-red-400 mx-auto mb-3" />
              <p className="text-slate-600 font-medium">{error}</p>
              <button onClick={() => navigate("/login")} className="mt-4 text-blue-600 text-sm underline">
                Giriş sayfasına git
              </button>
            </div>
          ) : (
            <>
              <div className="mb-6">
                <h1 className="text-2xl font-bold text-slate-900 mb-1">Davet Kabul Et</h1>
                <p className="text-slate-500 text-sm">
                  <span className="font-medium text-slate-700">{inviteInfo.name}</span> olarak ekibe katılıyorsunuz
                </p>
              </div>

              {/* Info */}
              <div className="bg-blue-50 rounded-xl p-4 mb-6 space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <span className="text-slate-500 w-16">İsim</span>
                  <span className="font-medium text-slate-800">{inviteInfo.name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-slate-500 w-16">E-posta</span>
                  <span className="font-medium text-slate-800">{inviteInfo.email}</span>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Password */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Şifre Oluştur</label>
                  <div className="relative">
                    <input
                      type={showPw ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full h-11 rounded-xl border border-slate-200 px-4 pr-11 text-sm focus:outline-none focus:border-blue-400"
                      placeholder="Güçlü şifre girin"
                      data-testid="invite-password-input"
                      autoComplete="new-password"
                    />
                    <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                      {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>

                  {/* Strength rules */}
                  {password && (
                    <div className="mt-2.5 grid grid-cols-2 gap-1">
                      {RULES.map((rule) => (
                        <div key={rule.label} className={`flex items-center gap-1.5 text-xs ${rule.test(password) ? "text-emerald-600" : "text-slate-400"}`}>
                          {rule.test(password) ? <CheckCircle size={11} /> : <XCircle size={11} />}
                          {rule.label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Confirm */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Şifre Tekrar</label>
                  <input
                    type={showPw ? "text" : "password"}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className={`w-full h-11 rounded-xl border px-4 text-sm focus:outline-none ${
                      confirm && !passwordsMatch ? "border-red-300 focus:border-red-400" :
                      confirm && passwordsMatch ? "border-emerald-300 focus:border-emerald-400" :
                      "border-slate-200 focus:border-blue-400"
                    }`}
                    placeholder="Şifreyi tekrar girin"
                    data-testid="invite-confirm-input"
                    autoComplete="new-password"
                  />
                  {confirm && !passwordsMatch && (
                    <p className="text-xs text-red-500 mt-1">Şifreler eşleşmiyor</p>
                  )}
                </div>

                {submitError && (
                  <div className="bg-red-50 border border-red-100 rounded-xl p-3 text-sm text-red-700">{submitError}</div>
                )}

                <button
                  type="submit"
                  disabled={submitting || !allRulesPassed || !passwordsMatch}
                  className="w-full h-11 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 shadow-lg shadow-blue-500/25"
                  data-testid="invite-submit-btn"
                >
                  {submitting ? "Hesap oluşturuluyor..." : "Hesabı Oluştur ve Giriş Yap"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
